import React, { useId } from "react";
import { Link } from "react-router-dom";
import Text from "../../../common/typography/text.js";
import ChangeIndicator from "../../../common/change-indicator/change-indicator.js";
import Button from "../../../common/button/button.js";
import {
  formatCurrency,
  formatUOM,
  formatGrowth,
  growthVariant,
} from "../aabiFormatters";
import { selectedMetricKey } from "../aabiViewModel";
import CompactCurrency from "./CompactCurrency";
import { MEASURE_LABELS, MEASURE_METRIC_KEY } from "../aabiConstants";
import "./../AABI.css";

// The five scoped KPI tiles (§5.4). Every value reflects the current drill
// scope, date range and revenue measure; supporting context shows the exact
// prior value / comparison so no percentage is ever unexplained (§4.4).
//
// There is deliberately no standalone "Growth vs prior" tile: its value was a
// chip rather than a number, which broke the row's rhythm, and it repeated the
// chip already sitting inside the revenue tile. The comparison window is
// stated once in the toolbar instead.
//
// Typography and growth indicators follow the existing Card/QuickList
// conventions (Text variants + ChangeIndicator).
//
// The Executive Dashboard reuses this grid through additive props whose
// defaults keep the manufacturer page exactly as it was:
//   links           per-tile URLs (keys: revenue, invoiced, uom, accounts,
//                   invoices). A linked tile is ONE link, with no nested
//                   buttons, and its figures are static text.
//   captions        extra line under a tile's context (keys: revenue, uom)
//   countPriors     show "Prior" + change on the Accounts and Invoices tiles
//   invoicedChange  { label, variant } change of the Invoiced %, in points
//   definitions     one-sentence metric definitions (keys as links)
//   ariaLabel       the section's accessible name
const TILE_ACTION_LABEL = {
  revenue: "View invoices",
  invoiced: "View invoices",
  uom: "View invoices",
  accounts: "View accounts",
  invoices: "View invoices",
};

// Small SVG ring: the arc is the filled share, the rest is the track.
const MiniDonut = ({ ratio, size = 28, stroke = 5 }) => {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  return (
    <svg
      className="aabi-mini-donut"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden="true"
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--strokeLight)"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#2663EB"
        strokeWidth={stroke}
        strokeDasharray={`${ratio * circumference} ${circumference}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
};

const growthPhrase = (growth) => {
  if (growth === null || growth === undefined || !Number.isFinite(growth)) {
    return "new versus prior period";
  }
  if (growth === 0) return "flat versus prior period";
  return `${growth > 0 ? "up" : "down"} ${Math.abs(growth * 100).toFixed(1)}% versus prior period`;
};

const ScopedMetricGrid = ({
  totals,
  measure,
  loading,
  onViewInvoices,
  links,
  captions,
  countPriors = false,
  invoicedChange,
  definitions,
  ariaLabel = "Scoped key metrics",
}) => {
  const idPrefix = useId();
  const metricKey = MEASURE_METRIC_KEY[measure] || selectedMetricKey(measure);
  const revenue = totals?.[metricKey] ?? 0;
  const priorRevenue = totals?.prior?.[metricKey] ?? 0;
  const growth = totals?.growth?.[metricKey] ?? null;
  const uomGrowth = totals?.growth?.uom ?? null;
  const priorUom = totals?.prior?.uom ?? 0;

  // Realized as a share of Current, independent of the selected measure.
  // Undefined when Current is 0, so say so rather than showing Infinity/NaN.
  const currentTotal = Number(totals?.currentRevenue ?? 0);
  const realizedTotal = Number(totals?.realizedRevenue ?? 0);
  const realizedVsCurrent =
    currentTotal > 0 && Number.isFinite(realizedTotal)
      ? `${((realizedTotal / currentTotal) * 100).toFixed(0)}%`
      : "No sales";

  // Ring fill is clamped to 0-100%; the text keeps the true figure.
  const invoicedRatio =
    currentTotal > 0 && Number.isFinite(realizedTotal)
      ? Math.min(1, Math.max(0, realizedTotal / currentTotal))
      : 0;

  const invoiceCount = totals?.invoiceCount ?? 0;
  const accountCount = totals?.accountCount ?? 0;
  const invoiceGrowth = totals?.growth?.invoiceCount ?? null;
  const accountGrowth = totals?.growth?.accountCount ?? null;

  // One tile shell: a plain box, or a single link when the tile has a target.
  const renderTile = (key, children, ariaLabelText) => {
    const to = links?.[key];
    const definition = definitions?.[key];
    const definitionId = `${idPrefix}-${key}-definition`;
    const body = (
      <>
        {children}
        {definition && (
          <span id={definitionId} className="aabi-visually-hidden">
            {definition}
          </span>
        )}
        {to && !loading && (
          <span className="aabi-kpi-tile-action" aria-hidden="true">
            {`${TILE_ACTION_LABEL[key]} →`}
          </span>
        )}
      </>
    );
    if (to) {
      return (
        <Link
          to={to}
          className="aabi-kpi-tile aabi-kpi-tile-link"
          aria-label={loading ? undefined : `${ariaLabelText}. ${TILE_ACTION_LABEL[key]}`}
          aria-describedby={definition ? definitionId : undefined}
        >
          {body}
        </Link>
      );
    }
    return <div className="aabi-kpi-tile">{body}</div>;
  };

  const isLinked = (key) => Boolean(links?.[key]);

  const label = (text, key) => (
    <div className="aabi-kpi-label">
      <Text variant="tinyText" color="lightText">
        {text}
      </Text>
      {definitions?.[key] && (
        <span className="aabi-kpi-definition" title={definitions[key]} aria-hidden="true">
          i
        </span>
      )}
    </div>
  );

  return (
    <section className="aabi-kpi-grid" aria-busy={loading} aria-label={ariaLabel}>
      {renderTile(
        "revenue",
        <>
          {label(MEASURE_LABELS[measure], "revenue")}
          {loading ? (
            <div className="aabi-kpi-skeleton" aria-hidden="true" />
          ) : (
            // Compact notation is display-only; the full-precision prior value
            // is shown in the supporting context below (§5.4).
            <CompactCurrency
              value={revenue}
              variant="numberBig"
              className="aabi-kpi-value"
              staticValue={isLinked("revenue")}
            />
          )}
          {!loading && (
            <div className="aabi-kpi-context">
              <CompactCurrency
                value={priorRevenue}
                prefix="Prior: "
                variant="tinyText"
                color="lightText"
                staticValue={isLinked("revenue")}
              />
              <ChangeIndicator
                variant={growthVariant(growth)}
                percent={formatGrowth(growth)}
              />
            </div>
          )}
          {!loading && captions?.revenue && (
            <Text variant="tinyText" color="lightText" className="aabi-kpi-caption">
              {captions.revenue}
            </Text>
          )}
        </>,
        `${MEASURE_LABELS[measure]} ${formatCurrency(revenue)}, ${growthPhrase(growth)}`,
      )}

      {renderTile(
        "invoiced",
        <>
          {label("Invoiced", "invoiced")}
          {loading ? (
            <div className="aabi-kpi-skeleton" aria-hidden="true" />
          ) : (
            <div className="aabi-kpi-value-row">
              <Text variant="numberBig" className="aabi-kpi-value">
                {realizedVsCurrent}
              </Text>
              <MiniDonut ratio={invoicedRatio} />
            </div>
          )}
          {!loading && (
            <div className="aabi-kpi-context">
              {invoicedChange ? (
                <>
                  <ChangeIndicator
                    variant={invoicedChange.variant}
                    percent={invoicedChange.label}
                  />
                  {currentTotal > 0 && (
                    <span className="aabi-kpi-context-inline">
                      <CompactCurrency
                        value={realizedTotal}
                        prefix="Realized "
                        variant="tinyText"
                        color="lightText"
                        staticValue={isLinked("invoiced")}
                      />
                      <Text variant="tinyText" color="lightText">
                        of
                      </Text>
                      <CompactCurrency
                        value={currentTotal}
                        variant="tinyText"
                        color="lightText"
                        staticValue={isLinked("invoiced")}
                      />
                    </span>
                  )}
                </>
              ) : (
                <Text variant="tinyText" color="lightText">
                  Realized Sales / Current Sales
                </Text>
              )}
            </div>
          )}
        </>,
        `Invoiced ${realizedVsCurrent}${
          invoicedChange ? `, ${invoicedChange.label} versus prior period` : ""
        }`,
      )}

      {renderTile(
        "uom",
        <>
          {label("UOM", "uom")}
          {loading ? (
            <div className="aabi-kpi-skeleton" aria-hidden="true" />
          ) : (
            <Text variant="numberBig" className="aabi-kpi-value">
              {formatUOM(totals?.uom ?? 0)}
            </Text>
          )}
          {!loading && (
            <div className="aabi-kpi-context">
              <Text variant="tinyText" color="lightText">
                {`Prior: ${formatUOM(priorUom)}`}
              </Text>
              <ChangeIndicator
                variant={growthVariant(uomGrowth)}
                percent={formatGrowth(uomGrowth)}
              />
            </div>
          )}
          {!loading && captions?.uom && (
            <Text variant="tinyText" color="lightText" className="aabi-kpi-caption">
              {captions.uom}
            </Text>
          )}
        </>,
        `UOM ${formatUOM(totals?.uom ?? 0)}, ${growthPhrase(uomGrowth)}`,
      )}

      {renderTile(
        "accounts",
        <>
          {label("Accounts", "accounts")}
          {loading ? (
            <div className="aabi-kpi-skeleton" aria-hidden="true" />
          ) : (
            <Text variant="numberBig" className="aabi-kpi-value">
              {formatUOM(accountCount)}
            </Text>
          )}
          {!loading && (
            <div className="aabi-kpi-context">
              {countPriors ? (
                <>
                  <Text variant="tinyText" color="lightText">
                    {`Prior: ${formatUOM(totals?.prior?.accountCount ?? 0)}`}
                  </Text>
                  <ChangeIndicator
                    variant={growthVariant(accountGrowth)}
                    percent={formatGrowth(accountGrowth)}
                  />
                </>
              ) : (
                <Text variant="tinyText" color="lightText">
                  In selected scope
                </Text>
              )}
            </div>
          )}
        </>,
        `Accounts ${formatUOM(accountCount)}, ${growthPhrase(accountGrowth)}`,
      )}

      {renderTile(
        "invoices",
        <>
          {label("Invoices", "invoices")}
          {loading ? (
            <div className="aabi-kpi-skeleton" aria-hidden="true" />
          ) : links?.invoices ? (
            <Text variant="numberBig" className="aabi-kpi-value">
              {formatUOM(invoiceCount)}
            </Text>
          ) : onViewInvoices ? (
            <Button variant="link" onClick={onViewInvoices}>
              <Text variant="numberBig" color="primaryColor">
                {formatUOM(invoiceCount)}
              </Text>
            </Button>
          ) : (
            <Text variant="numberBig" className="aabi-kpi-value">
              {formatUOM(invoiceCount)}
            </Text>
          )}
          {!loading && countPriors && (
            <div className="aabi-kpi-context">
              <Text variant="tinyText" color="lightText">
                {`Prior: ${formatUOM(totals?.prior?.invoiceCount ?? 0)}`}
              </Text>
              <ChangeIndicator
                variant={growthVariant(invoiceGrowth)}
                percent={formatGrowth(invoiceGrowth)}
              />
            </div>
          )}
          {!loading && !countPriors && onViewInvoices && (
            <div className="aabi-kpi-context">
              <Text variant="tinyText" color="lightText">
                View these invoices
              </Text>
            </div>
          )}
        </>,
        `Invoices ${formatUOM(invoiceCount)}, ${growthPhrase(invoiceGrowth)}`,
      )}
    </section>
  );
};

export default ScopedMetricGrid;
